import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import _ from 'lodash';

import { Grid } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';

import * as statisticsReducer from '../reducer';
import * as statisticsActionMgr from '../action-mgr';

import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import '../../index.css';

class StationStatisticsGraphBiDirectionalEnergy extends Component {

    calculateChartOptions = (selectedView) => {
        switch (selectedView) {
            case "half-hour":
                return { 
                    groupPadding: 0.05, 
                    pointWidth: 6 
                };
            case "day":
                return {
                    groupPadding: 0.1,
                    pointWidth: 14 
                };
            case "month":
                return {
                    groupPadding: 0.08,
                    pointWidth: 10 
                };
            case "year":
                return {
                    groupPadding: 0.1,
                    pointWidth: 30 
                };
            case "all":
                return {
                    groupPadding: 0.4,
                    pointWidth: 40 
                };
            default:
                return {
                    groupPadding: 0.4,
                    pointWidth: 55 // default width and padding
                };
        }
    }
    
    constructor(props) {
        super(props);
        this.chartRef = React.createRef();  // Create the ref for the chart
        this.state = {
            options: {
                title: {
                    text: '',
                },
                plotOptions: {
                    column: {
                        grouping: true,
                        stacking: 'normal', // Enable stacking
                        groupPadding: 0.42, // control group spacing
                        pointPadding: 0.40, // control bar spacing
                        pointWidth: 55,
                        dataLabels: {
                            enabled: true,
                            formatter: function () {
                                let value = Math.abs(this.y);  // Show absolute value in data labels
                                return value.toLocaleString();
                            },
                        }
                    },
                },
                credits: {
                    enabled: false,
                },
                exporting: {
                    enabled: true,
                    buttons: {
                    contextButton: {
                        menuItems: ['viewFullscreen'] // only this item will show
                    }
                    }
                }
            },
            importExportSeries: [],
        };
    }

    componentDidMount() {
        this.initChart(); // Initialize the chart when the component is first mounted
    }

    componentDidUpdate(prevProps) {
        const { selectedStationName, biDirectionalEnergyData, biDirectionalEnergyFilter } = this.props;
    
        if (
            prevProps.selectedStationName !== selectedStationName ||
            (!_.isEqual(prevProps.biDirectionalEnergyData, biDirectionalEnergyData) && biDirectionalEnergyData) ||
            (!_.isEqual(prevProps.biDirectionalEnergyFilter, biDirectionalEnergyFilter) && biDirectionalEnergyFilter)
        ) {
            this.initChart();
        }
    }
    
    initChart = () => {
        const { biDirectionalEnergyData, biDirectionalEnergyFilter } = this.props;
        const selectedView = biDirectionalEnergyFilter.type;
        const selectedTime = biDirectionalEnergyFilter.time;
        const selectedFacilities = biDirectionalEnergyFilter.facilities;

        // Check if selectedTime is valid based on the selectedView
        if (
            (selectedView === 'year' && !selectedTime) ||
            (selectedView === 'month' && !selectedTime) ||
            (selectedView === 'day' && !selectedTime)
        ) {
            this.setState({
                importExportSeries: [] // Clear the series if time is not selected
            });
            return; // Exit the method if time is not set
        }
        
        if (!biDirectionalEnergyData || biDirectionalEnergyData.length === 0 || !selectedFacilities.length) {
            this.setState({
                importExportSeries: [] // Clear the series if data is not available
            });
            return;
        }
    
        // Process data for the chart
        this.processChartData(selectedView, selectedTime, biDirectionalEnergyData, selectedFacilities);    
    }    

    processChartData = (selectedView, selectedTime, biDirectionalEnergyData, selectedFacilities) => {

        let cloneImportExportSeries = [];
        let groupedData = _.groupBy(biDirectionalEnergyData, 'facility');
    
        // Sort and filter facilities by the selected ones
        const sortedFacilities = selectedFacilities.sort();
    
        cloneImportExportSeries = sortedFacilities.flatMap((facility) => {
            const data = groupedData[facility] || []; // Handle if no data for a facility
            let importData = [];
            let exportData = [];
            let solarGenData = [];
            let buildingConsumptionData = [];
    
            if (selectedView === "all") {
                // Group by year
                const years = [...new Set(data.map(d => d.time))];
                importData = new Array(years.length).fill(0);
                exportData = new Array(years.length).fill(0);
                solarGenData = new Array(years.length).fill(0);
                buildingConsumptionData = new Array(years.length).fill(0);
                data.forEach(d => {
                    const yearIndex = years.indexOf(d.time);
                    if (yearIndex !== -1) {
                        importData[yearIndex] += d.energyImport || 0;
                        exportData[yearIndex] += -(d.energyExport || 0);
                        solarGenData[yearIndex] += d.solarGen || 0;
                        buildingConsumptionData[yearIndex] += d.buildingConsumption || 0;
                    }
                });
            } else if (selectedView === "year") {
                // Group by month
                importData = new Array(12).fill(0);
                exportData = new Array(12).fill(0);
                solarGenData = new Array(12).fill(0);
                buildingConsumptionData = new Array(12).fill(0);
                data.forEach(d => {
                    const monthIndex = parseInt(d.time.split("-")[1], 10) - 1;
                    importData[monthIndex] = d.energyImport;
                    exportData[monthIndex] = -(d.energyExport);
                    solarGenData[monthIndex] = d.solarGen;
                    buildingConsumptionData[monthIndex] = d.buildingConsumption;
                });
            } else if (selectedView === "month") {
                // Group by day
                let daysInMonth = 31;
                const [year, month] = selectedTime.split("-");
                if (year && month) {
                    daysInMonth = new Date(parseInt(year, 10), parseInt(month, 10), 0).getDate();
                    importData = new Array(daysInMonth).fill(0);
                    exportData = new Array(daysInMonth).fill(0);
                    solarGenData = new Array(daysInMonth).fill(0);
                    buildingConsumptionData = new Array(daysInMonth).fill(0);
                    data.forEach(d => {
                        const dayIndex = parseInt(d.time.split("-")[2], 10) - 1;
                        if (dayIndex >= 0 && dayIndex < daysInMonth) {
                            importData[dayIndex] = d.energyImport;
                            exportData[dayIndex] = -(d.energyExport);
                            solarGenData[dayIndex] = d.solarGen;
                            buildingConsumptionData[dayIndex] = d.buildingConsumption;
                        }
                    });
                }
            } else if (selectedView === "day") {
                // Group by hour
                importData = new Array(24).fill(0);
                exportData = new Array(24).fill(0);
                solarGenData = new Array(24).fill(0);
                buildingConsumptionData = new Array(24).fill(0);
    
                data.forEach(d => {
                    if (d.time && d.time.includes("T")) {
                        const timeParts = d.time.split("T");
                        if (timeParts.length >= 2 && timeParts[1].includes(":")) {
                            const hourIndex = parseInt(timeParts[1].split(":")[0], 10);
                            if (!isNaN(hourIndex) && hourIndex >= 0 && hourIndex < 24) {
                                importData[hourIndex] = d.energyImport || 0;
                                exportData[hourIndex] = -(d.energyExport || 0);
                                solarGenData[hourIndex] = d.solarGen || 0;
                                buildingConsumptionData[hourIndex] = d.buildingConsumption || 0;
                            }
                        }
                    }
                });
            }else if (selectedView === "half-hour") {
                // Create 48 half-hour slots
                importData = new Array(48).fill(0);
                exportData = new Array(48).fill(0);
                solarGenData = new Array(48).fill(0);
                buildingConsumptionData = new Array(48).fill(0);

                data.forEach(d => {
                    if (d.time && d.time.includes("T")) {
                        const [date, timePart] = d.time.split("T");
                        if (timePart) {
                            const [hour, minute] = timePart.split(":").map(x => parseInt(x, 10));
                            const slotIndex = hour * 2 + (minute >= 30 ? 1 : 0); // 0–47 range
                            if (!isNaN(slotIndex) && slotIndex < 48) {
                                importData[slotIndex] = d.energyImport || 0;
                                exportData[slotIndex] = -(d.energyExport || 0);
                                solarGenData[slotIndex] = d.solarGen || 0;
                                buildingConsumptionData[slotIndex] = d.buildingConsumption || 0;
                            }
                        }
                    }
                });
            }
    
            return [
                {
                    name: `${facility} Building Consumption`,
                    data: buildingConsumptionData,
                    stack: null,
                    index: 1
                },
                {
                    name: `${facility} Export`,
                    data: exportData,
                    stack: facility,
                    index: 2
                },
                {
                    name: `${facility} Solar Consumption`,
                    data: solarGenData,
                    stack: facility,
                    index: 3
                },
                {
                    name: `${facility} Grid Consumption (Import)`,
                    data: importData,
                    stack: facility,
                    index: 4
                }
            ];
        });
    
        if (cloneImportExportSeries.length > 0) {
            this.setState({
                importExportSeries: cloneImportExportSeries
            }, () => {
                if (this.chartRef.current) {
                    this.chartRef.current.chart.redraw(); // Check if chartRef exists before using it
                }
            });
        }
    };    

    getImportExportGradientColor(type) {

        if (type === 'Grid Consumption (Import)') {
            return {
                linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                stops: [
                    [0, '#59C1BD'], 
                    [1, '#3E54AC']  

                ]
            };
        } else if (type === 'Export') {
            return {
                linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                stops: [
                    [0, '#FFD700'],
                    [1, '#FFA500']  
                ]
            };
        } else if (type === 'Building Consumption') {
            return {
                linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                stops: [
                    [0, '#8c9093'], 
                    [1, '#343A40'], 
                    
                ]
            };
        } else if (type === 'Solar Consumption') {
            return {
                linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
                stops: [
                    [0, '#b2d78f'],  // Bright green
                    [1, '#008000']   // Dark green
                ]
            };
        }
    }
    

    render() {
        const { biDirectionalEnergyFilter, biDirectionalEnergyData } = this.props;
        const { importExportSeries } = this.state;

       const selectedView = biDirectionalEnergyFilter.type;  // Initialize selectedView here
       const selectedTime = biDirectionalEnergyFilter.time;
       
       const chartOptions = this.calculateChartOptions(selectedView);  

       const monthDays = selectedView === "month" && selectedTime 
           ? new Date(parseInt(selectedTime.slice(0, 4), 10), parseInt(selectedTime.slice(5, 7), 10), 0).getDate()
           : 0;
       
        // Determine which message to display
        let message = '';
        if ((selectedView === 'year' || selectedView === 'month' || selectedView === 'day') && !selectedTime) {
            message = 'No time is selected. Please select a time to display the data.';
        } else if (!biDirectionalEnergyData || biDirectionalEnergyData.length === 0) {
            message = 'Bi Directional Energy Data isn\'t available.';
        }
        
        return (
            <Grid container alignItems="center">
                <Grid item xs={12} style={{ margin: "1px 10px" }}>
                    {importExportSeries.length > 0 ? (
                        <Paper style={{ paddingTop: 15 }}>
                            <HighchartsReact
                                highcharts={Highcharts}
                                ref={this.chartRef}  // Attach the ref to HighchartsReact
                                options={{
                                    ...this.state.options,
                                    chart: {
                                        type: "column",
                                        height: 650,
                                    },
                                    title: {
                                        text: []
                                    },
                                    xAxis: {
                                    categories:
                                        selectedView === "all"
                                        ? (biDirectionalEnergyData && Array.isArray(biDirectionalEnergyData)
                                            ? [...new Set(biDirectionalEnergyData.map(d => d.time))]
                                            : [])
                                        : selectedView === "year"
                                        ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
                                        : selectedView === "month"
                                        ? Array.from({ length: monthDays }, (_, i) => (i + 1).toString())
                                        : selectedView === "day"
                                        ? Array.from({ length: 24 }, (_, i) => `${i}:00`)
                                        : selectedView === "half-hour"
                                        ? Array.from({ length: 48 }, (_, i) => {
                                            const hour = Math.floor(i / 2);
                                            const minute = i % 2 === 0 ? "00" : "30";
                                            return `${hour.toString().padStart(2, "0")}:${minute}`;
                                            })
                                        : [], 
                                        allowDecimals: false,
                                        title: {
                                        text:
                                            selectedView === "all"
                                            ? "Years"
                                            : selectedView === "year"
                                            ? "Months"
                                            : selectedView === "month"
                                            ? "Days"
                                            : selectedView === "day"
                                            ? "Hours"
                                            : selectedView === "half-hour"
                                            ? "Half-Hour"
                                            : "Days"
                                        }

                                    },
                                    yAxis: {
                                        title: {
                                            text: 'kWh',
                                        },
                                        labels: {
                                            formatter: function () {
                                                // Display positive value even for negative numbers
                                                return Math.abs(this.value).toLocaleString(); // Use absolute value for labels
                                            }
                                        },
                                        stackLabels: {
                                            enabled: true,
                                            style: {
                                                fontWeight: 'bold',
                                                color: 'black',
                                                textOutline: '1px white',
                                                textAlign: 'center',
                                                fontSize: '10px',
                                                fontWeight: 'bold',
                                            },
                                            formatter: function () {
                                                const stackName = this.stack; // Access the stack name (facility)
                                                // If stackName is null or undefined, return an empty string (hide the label)
                                                if (!stackName) {
                                                    return '';
                                                }
                                    
                                                // Map stackName to a shortened version (M1, M2, etc.)
                                                const shortName = stackName.replace(/BiDirectionalMeter/, 'M'); // Change "BiDirectionalMeter1" to "M1"
                                                return shortName;
                                            }
                                        },
                                        min: null, // Allow the chart to automatically adjust based on the data, including negative values
                                    },                                    
                                    tooltip: {
                                        formatter: function () {
                                            let value = Math.abs(this.y);  // Use absolute value for the tooltip
                                            const shortName = this.series.name.replace(/BiDirectionalMeter/, 'M'); // Replace "BiDirectionalMeter" with "M"
                                            return `<b>${this.x}</b><br/>${shortName}: ${value.toLocaleString()} kWh`;
                                        }
                                    },
                                    plotOptions: {
                                        column: {
                                            grouping: true,
                                            stacking: 'normal',
                                            groupPadding: chartOptions.groupPadding,
                                            pointWidth: chartOptions.pointWidth, 
                                            dataLabels: {
                                                enabled: true,
                                                useHTML: false, 
                                                inside: true, // Display inside the bar
                                                align: 'center', // Align in the center of the bar
                                                formatter: function () {
                                                    const seriesName = this.series.name; // Access the series name directly
                                    
                                                    // Only show data labels for the "Building Consumption" series
                                                    if (seriesName.includes("Building Consumption")) {
                                                        const stackName = this.series.name.split(' ')[0]; // Extract the meter part (BiDirectionalMeter1, etc.)
                                                        const shortName = stackName.replace(/BiDirectionalMeter/, 'M'); // Change "BiDirectionalMeter1" to "M1"
                                                        const value = Math.abs(this.y).toLocaleString(); // Format the value
                                    
                                                        // Return both meter name (M1, M2) and value in the data label for "Building Consumption"
                                                        return `${shortName}<br/>${value}`;
                                                        // return `<div style="text-align: center;">
                                                        //     <span style="color: black !important; font-weight: bold !important; font-size: 10px !important; text-outline: 1px white !important;">${shortName}</span><br/>
                                                        //     <span style="color: black !important; font-weight: bold !important; font-size: 10px !important; text-outline: 1px white !important;">${value}</span>
                                                        // </div>`;
                                            

                                                    }
                                    
                                                    // For other series, show only the value with white outline
                                                    return Math.abs(this.y).toLocaleString();
                                                },
                                                style: {
                                                    textAlign: 'center',
                                                    fontSize: '10px',
                                                    fontWeight: 'bold',
                                                    color: 'black', 
                                                    textOutline: '1px white' 
                                                },
                                                rotation: 0, // Rotate labels to display them vertically
                                                overflow: 'allow', // Allow labels to overflow the bars
                                                crop: false, // Prevent cropping of labels
                                                allowOverlap: true, // Allow labels to overlap
                                            }
                                        }
                                    },                                
                                    // series: importExportSeries.map((series) => ({
                                    //     ...series,
                                    //     color: this.getImportExportGradientColor(series.name.includes('Grid Consumption (Import)') ? 'Grid Consumption (Import)' :
                                    //                                              series.name.includes('Export') ? 'Export' :
                                    //                                              series.name.includes('Solar Consumption') ? 'Solar Consumption' :
                                    //                                              series.name.includes('Building Consumption') ? 'Building Consumption' : '')
                                    // }))            
                                    series: importExportSeries.map((series) => {
                                        return {
                                            ...series,
                                            color: this.getImportExportGradientColor(series.name.includes('Grid Consumption (Import)') ? 'Grid Consumption (Import)' :
                                                                                     series.name.includes('Export') ? 'Export' :
                                                                                     series.name.includes('Solar Consumption') ? 'Solar Consumption' :
                                                                                     series.name.includes('Building Consumption') ? 'Building Consumption' : '')
                                        };
                                    })
                                                         
                                }}
                            />
                        </Paper>
                    ) : (
                        <Paper style={{ padding: 20, textAlign: 'center' }}>
                        <p style={{ margin: 0, color: '#999', fontSize: '16px' }}>
                            {message}
                        </p>
                        </Paper>
                    )}
                </Grid>
            </Grid>
        );
    }
}

const mapStateToProps = state => ({
    selectedStationName: statisticsReducer.getSelectedStatisticsStationName(state),
    biDirectionalEnergyData: statisticsReducer.getBiDirectionalEnergyData(state),
    biDirectionalEnergyFilter: statisticsReducer.getBiDirectionalEnergyFilter(state),
    statisticsFilter: statisticsReducer.getStatisticsFilter(state),
});

const mapActionsToProps = {
    setselectStatisticsStation: statisticsActionMgr.setSelectStatisticsStation,
    getStationTimeOffset: statisticsActionMgr.getStationTimeOffset,
    setSelectYear: statisticsActionMgr.setSelectYear,
};

export default connect(mapStateToProps, mapActionsToProps)(
    withRouter(StationStatisticsGraphBiDirectionalEnergy)
);